/**
 * White-label public site config — the whitelist projection returned by
 * svc-tenancy GET /api/v1/site-config (no JWT). NEVER carries secrets
 * (CAPTCHA_SECRET, ancestors, chain, parentId): see docs/...white-label spec §4.1.
 */
import { t } from 'elysia';
import type { Static } from '@sinclair/typebox';

export type PublicSiteConfig = {
  tenantId: string;
  agentId: string;
  /** display_name with a neutral fallback applied by the projection. */
  sitename: string;
  logoUrl: string | null;
  logoDarkUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  defaultLocale: string | null;
  /** ISO 3166-1 alpha-2 — fixed phone country for the register form. */
  phoneCountry: string | null;
  /** What the site SHOWS money in (the storefront's display currency). */
  currency: string;
  /**
   * What the brand's money is HELD in. Equal to `currency` on a single-currency
   * brand; differs on a brand that displays e.g. LAK over a THB ledger.
   */
  ledgerCurrency: string;
  /** siteKey = CAPTCHA_SITE_KEY (public, safe to ship to the browser). */
  captcha: { enabled: boolean; siteKey: string | null };
  /** websiteToken is a public Chatwoot inbox token; baseUrl from platform env. */
  chatwoot: { baseUrl: string; websiteToken: string } | null;
  support: { lineId?: string } | null;
  /**
   * The storefront's primary active hostname (#595) — where generated links
   * (referral, share) should point, since the host the player is on may be one
   * an ISP is about to block. Null until a primary is set; fall back to the
   * current origin.
   */
  primaryDomain: string | null;
};

/**
 * Validates the `organizations.settings` jsonb at the HTTP boundary.
 * additionalProperties:false so a stray secret key cannot ride in.
 */
export const OrgSettings = t.Object(
  {
    captcha: t.Optional(t.Object({ enabled: t.Boolean() }, { additionalProperties: false })),
    chatwoot: t.Optional(
      t.Object({ websiteToken: t.String({ maxLength: 64 }) }, { additionalProperties: false }),
    ),
    support: t.Optional(
      t.Object(
        { lineId: t.Optional(t.String({ maxLength: 64 })) },
        { additionalProperties: false },
      ),
    ),
  },
  { additionalProperties: false },
);

export type OrgSettings = Static<typeof OrgSettings>;
